<template>
  <div>
    <div class="position-relative">
      <div class="workspace-title">
        Settings
      </div>
    </div>
    <b-row>
      <b-col tag="main" class="mb-4">
        <div class="bg-white p-md-5 p-4">
          <div class="mb-5">
            <h5 class="color-deeppink font-weight-bold mb-4">Workspace URL</h5>
            <p>The workspace URL will be used to allow people to build teams and recognize each other.</p>
            <p>Make sure you always have access to it</p>
            <div class="p-3 bg-color-lightgray2 font-weight-bold">
              {{workspaceUrl}}
            </div>
            <div class="mb-5">
              <h5 class="color-deeppink font-weight-bold mt-5 mb-4">Integrations</h5>
              <div v-if="slackState" class="mb-4">
                <add-to-slack-link v-if="showAddToSlack" :href="slackState.authUrl" />
                <div v-else class="font-weight-bold">
                  Added to Slack
                </div>
              </div>
              <div v-if="msTeamsState">
                  MS Teams integration:
                  <strong v-if="msTeamsState.enabled">enabled</strong>
                  <strong v-else>disabled</strong>
                  <span v-if="msTeamsState.secretKey">. Secret key: <strong>{{ msTeamsState.secretKey }}</strong></span>
              </div>
            </div>
          </div>
          <div class="mb-5">
          <h5 class="color-deeppink font-weight-bold mb-4">Workspace Administrator</h5>
            <p>Administrators are in charge of the workspace and its configuration.</p>
            <template v-if="isTeamsLoaded">
              <template v-if="canEditAdmins">
                <manage-members
                  :initial-members="computedWorkspaceAdmins"
                  :profiles="profiles"
                  keep-last
                >
                  <template #current-label>
                    Current workspace administrators
                  </template>
                  <template #bottom="{ changes, isChanged, cancel }">
                    <b-row v-if="isChanged">
                      <b-col cols="auto">
                        <b-button :disabled="processingAdmins" variant="primary" class="px-md-5 px-4" @click="onAdminsApply(changes)">
                          Apply
                        </b-button>
                      </b-col>
                      <b-col cols="auto">
                        <b-button :disabled="processingAdmins" variant="outline-primary" class="px-md-5 px-4" @click="cancel()">
                          Cancel
                        </b-button>
                      </b-col>
                    </b-row>
                  </template>
                </manage-members>
              </template>
            </template>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { AddToSlackLink } from '@/components/common/Slack'
import ManageMembers from '@/components/common/ManageMembers'
import statuses from '@/helpers/statuses'
import { toastError } from '@/helpers/helpers'

export default {
  name: 'Settings',

  data() {
    return {
      companySlug: '',
      processingAdmins: false
    }
  },

  computed: {
    ...mapGetters([
      'slackState',
      'msTeamsState',
      'workspaceAdmins',
      'profileMe',
      'profiles',
      'teamsLoading'
    ]),

    workspaceUrl() {
      return `${location.host}/${this.companySlug}`
    },

    canEditAdmins() {
      return this.workspaceAdmins.indexOf(this.profileMe.id) !== -1
    },

    isTeamsLoaded() {
      return this.teamsLoading === statuses.LOADED
    },

    computedWorkspaceAdmins() {
      return this.workspaceAdmins.map(profileId => ({ canDelete: true, profileId }))
    },

    showAddToSlack() {
      return this.slackState && !this.slackState.enabled
    }
  },

  async mounted() {
    this.companySlug = await this.$store.dispatch('getWorkspaceSlug')

    await Promise.all([
      this.$store.dispatch('slackState'),
      this.$store.dispatch('msTeamsState')
    ])
  },

  methods: {
    async onAdminsApply({ added, removed }) {
      const managemembers = [
        ...removed.map(id => this.$store.dispatch('deleteCompanyAdmin', id)),
        ...added.map(id => this.$store.dispatch('addCompanyAdmin', id))
      ]

      this.processingAdmins = true
      try {
        await Promise.all(managemembers)
      } catch (errors) {
        toastError(this, errors)
      } finally {
        this.processingAdmins = false
      }
    }
  },

  components: {
    AddToSlackLink,
    ManageMembers
  }
}
</script>
